<template>
  <div class="dropdown" ref="dropdownRef">
    <!-- Toggle button -->
    <button class="dropdown-button" @click="toggleOpen">
      <span class="label-text">
        <span v-if="displayedSelection.length">
          {{ displayedSelection.join(", ") }}
        </span>
        <span v-else>
          {{ buttonLabel }}
        </span>
      </span>
      <span class="arrow" :class="{ open: isOpen }">▾</span>
    </button>

    <!-- Dropdown menu -->
    <transition name="fade">
      <div v-if="isOpen" class="dropdown-menu">
        <!-- Search filter -->
        <div class="search-container">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search…"
            class="search-input"
            @keydown.stop
          />
        </div>

        <ul class="options-list">
          <!-- Always show all selected items first -->
          <li
            v-for="opt in fullSelectedOptions"
            :key="`sel-${opt.value}`"
            class="dropdown-item selected-item"
          >
            <label>
              <input
                type="checkbox"
                :value="opt.value"
                v-model="internalValue"
              />
              {{ opt.label }}
            </label>
          </li>

          <!-- Separator if both groups exist -->
          <li
            v-if="fullSelectedOptions.length && filteredUnselected.length"
            class="separator"
          ></li>

          <!-- Unselected items matching filter -->
          <li
            v-for="opt in filteredUnselected"
            :key="`unsel-${opt.value}`"
            class="dropdown-item"
          >
            <label>
              <input
                type="checkbox"
                :value="opt.value"
                v-model="internalValue"
              />
              {{ opt.label }}
            </label>
          </li>

          <!-- No matches -->
          <li
            v-if="!fullSelectedOptions.length && !filteredUnselected.length"
            class="no-results"
          >
            No matches
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script lang="js">
export default {
  name: "DatasetsManager",
  emits: ["update:modelValue"],
  props: {
    options: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
    buttonLabel: { type: String, default: "Select…" }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      internalValue: [...this.modelValue],
      selectedDatasetId: null,
    };
  },
  computed: {
    // base filtered by search, safe-guard options
    filteredOptions() {
      const opts = Array.isArray(this.options) ? this.options : [];
      const query = (this.searchQuery || "").trim().toLowerCase();
      if (!query) return opts;
      return opts.filter(opt =>
        String(opt.label).toLowerCase().includes(query)
      );
    },
    // always show all selected items
    fullSelectedOptions() {
      const opts = Array.isArray(this.options) ? this.options : [];
      return opts.filter(opt =>
        Array.isArray(this.internalValue) && this.internalValue.includes(opt.value)
      );
    },
    // unselected items that match the filter
    filteredUnselected() {
      const filtered = Array.isArray(this.filteredOptions) ? this.filteredOptions : [];
      return filtered.filter(opt =>
        !(Array.isArray(this.internalValue) && this.internalValue.includes(opt.value))
      );
    },
    displayedSelection() {
      const selected = Array.isArray(this.modelValue) ? this.modelValue : [];
      const lookup = new Map(this.options.map(o => [o.value, o.label]));
      return selected.map(val => lookup.get(val) || val);
    },
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    onClickOutside(e) {
      const dropdown = this.$refs.dropdownRef;
      if (dropdown && !dropdown.contains(e.target)) {
        this.isOpen = false;
      }
    },
    _arraysEqual(a, b) {
      if (a === b) return true;
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      return a.every((v, i) => v === b[i]);
    },
  },
  watch: {
    internalValue(val) {
      if (!this._arraysEqual(val, this.modelValue)) {
        this.$emit("update:modelValue", val);
      }
    },
    modelValue(val) {
      if (!this._arraysEqual(val, this.internalValue)) {
        this.internalValue = Array.isArray(val) ? [...val] : [];
      }
    },
    mounted() {
      document.addEventListener("click", this.onClickOutside);
    },
    beforeUnmount() {
      document.removeEventListener("click", this.onClickOutside);
    }
  }
};
</script>

<style scoped>
.dropdown {
  position: relative;
  display: inline-block;
}
.dropdown-button {
  padding: 0.5em 1em;
  cursor: pointer;
  user-select: none;
}
.label-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: middle;
  max-width: 160px;
}
.arrow {
  margin-left: 0.5em;
  transition: transform 0.2s;
}
.arrow.open {
  transform: rotate(180deg);
}
.dropdown-menu {
  position: absolute;
  margin-top: 0.25em;
  padding: 0.5em 0;
  background: white;
  border: 1px solid var(--colour-border);
  border-radius: 4px;
  width: 200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
}
.search-container {
  padding: 0 1em 0.5em;
}
.search-input {
  width: 100%;
  padding: 0.25em;
  box-sizing: border-box;
  border: 1px solid var(--colour-border-light);
  border-radius: 4px;
}
.options-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
}
.dropdown-item {
  padding: 0.25em 1em;
}
.dropdown-item label {
  cursor: pointer;
  display: flex;
  align-items: center;
}
.dropdown-item input {
  margin-right: 0.5em;
}
.separator {
  height: 1px;
  background: var(--colour-border-light);
  margin: 4px 0;
}
.no-results {
  padding: 0.5em 1em;
  color: #888;
  font-style: italic;
}
.selected-item label {
  font-weight: 500;
}
/* fade animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
